import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, TimedEntrySlot } from '../../api';
import { endpoints, timedEntryEndpoints } from '../../api';
import { Badge, Button, EmptyRow, Field, Panel, Table } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT } from './shared';
import { dateWords } from '../../terms';

/**
 * Entry times for one night (D-86, PART 23, 24, 25, 38).
 *
 * The operator's question at 7:40 on a Friday is never "what is the capacity
 * column set to" — it is "how is 8:15 doing, and can I put four more in it".
 * So this is the night as a list of times, each with what it sold, what is
 * held, who has walked in and what is genuinely left, and capacity is edited
 * one time or a whole night at once. Capacity is admissions, not a number: a
 * change here creates or disables real inventory, which is why a slot cannot
 * silently shrink below what it has already sold.
 */

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad'> = { OPEN: 'ok', SALES_STOPPED: 'warn', CLOSED: 'bad' };

const clock = (instant: string, timeZone: string) => new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(instant));
const night = (instant: string, timeZone: string) => new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(instant));

export function TimedEntryPanel({ detail, onError, can }: PanelProps) {
  const [performanceId, setPerformanceId] = useState(detail.performances[0]?.id ?? '');
  const performance = detail.performances.find((p) => p.id === performanceId) ?? null;
  const timezone = detail.venue.timezone;

  if (detail.performances.length === 0) {
    return (
      <Panel title="Entry times">
        <p className="px-5 py-4 text-sm text-ink-600">Add {dateWords().a} first; entry times belong to one.</p>
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-ink-900">
          Night{' '}
          <select value={performanceId} onChange={(e) => setPerformanceId(e.target.value)} className={INPUT}>
            {detail.performances.map((p) => (
              <option key={p.id} value={p.id}>
                {night(p.starts_at, timezone)} · {clock(p.starts_at, timezone)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {performance ? <Night key={performance.id} detail={detail} performanceId={performance.id} manifestId={performance.manifest_id} onError={onError} can={can} /> : null}
    </div>
  );
}

function Night({ detail, performanceId, manifestId, onError, can }: { detail: PanelProps['detail']; performanceId: string; manifestId: string; onError: PanelProps['onError']; can: PanelProps['can'] }) {
  const queryClient = useQueryClient();
  const eventId = detail.event.id;
  const timezone = detail.venue.timezone;
  const manage = can('timed_inventory.manage');

  const slots = useQuery({ queryKey: ['timed-entry', eventId, performanceId], queryFn: () => timedEntryEndpoints.slots(eventId, performanceId), refetchInterval: 15_000 });
  const layout = useQuery({ queryKey: ['manifest-layout', manifestId], queryFn: () => endpoints.manifestLayout(manifestId) });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCapacity, setBulkCapacity] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const refresh = () => {
    setSelected(new Set());
    void queryClient.invalidateQueries({ queryKey: ['timed-entry', eventId, performanceId] });
  };
  const fail = (cause: ApiError) => {
    setNote(null);
    onError(cause);
  };

  const patch = useMutation({
    mutationFn: ({ slotId, input }: { slotId: string; input: Record<string, unknown> }) => timedEntryEndpoints.updateSlot(eventId, slotId, input),
    onSuccess: (result) => {
      setNote(result.added ? `Added ${result.added} admissions.` : result.removed ? `Took ${result.removed} admissions off sale.` : 'Saved.');
      refresh();
    },
    onError: fail,
  });

  const bulk = useMutation({
    mutationFn: (input: { slot_ids: string[]; capacity?: number; status?: string }) => timedEntryEndpoints.bulk(eventId, input),
    onSuccess: (result) => {
      setNote(
        result.refused.length > 0
          ? `Changed ${result.changed.length} times. ${result.refused.length} kept their capacity because they have already sold more than that.`
          : `Changed ${result.changed.length} times.`,
      );
      refresh();
    },
    onError: fail,
  });

  const rows = slots.data?.slots ?? [];
  const totals = useMemo(
    () =>
      rows.reduce(
        (sum, slot) => ({
          capacity: sum.capacity + slot.units,
          sold: sum.sold + slot.sold,
          held: sum.held + slot.held,
          scanned: sum.scanned + slot.scanned,
          available: sum.available + slot.available,
        }),
        { capacity: 0, sold: 0, held: 0, scanned: 0, available: 0 },
      ),
    [rows],
  );

  if (slots.isPending) return <p className="text-sm text-ink-400">Loading…</p>;

  if (rows.length === 0) {
    return <Generator detail={detail} performanceId={performanceId} gaAreas={layout.data?.ga_areas ?? []} onDone={refresh} onError={fail} disabled={!manage} />;
  }

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-4">
      {note ? <p className="rounded border border-line bg-surface px-4 py-2 text-sm text-ink-700">{note}</p> : null}

      <Panel
        title={`Entry times · ${night(slots.data!.performance.starts_at, timezone)}`}
        description={`${rows.length} times · ${totals.capacity} admissions · ${totals.sold} sold · ${totals.available} left · ${totals.scanned} walked in`}
      >
        {manage ? (
          <div className="flex flex-wrap items-end gap-3 border-b border-line px-5 py-3">
            <p className="text-sm text-ink-600">
              {selected.size === 0 ? 'Tick times to change several at once.' : `${selected.size} selected`}
            </p>
            {selected.size > 0 ? (
              <>
                <Field label="Capacity each" htmlFor="bulk-capacity">
                  <input id="bulk-capacity" value={bulkCapacity} onChange={(e) => setBulkCapacity(e.target.value)} inputMode="numeric" className={`${INPUT} w-24`} />
                </Field>
                <Button
                  disabled={bulk.isPending || !bulkCapacity.trim()}
                  onClick={() => bulk.mutate({ slot_ids: [...selected], capacity: Number(bulkCapacity) })}
                >
                  Set capacity
                </Button>
                <Button variant="secondary" disabled={bulk.isPending} onClick={() => bulk.mutate({ slot_ids: [...selected], status: 'CLOSED' })}>
                  Close these
                </Button>
                <Button variant="secondary" disabled={bulk.isPending} onClick={() => bulk.mutate({ slot_ids: [...selected], status: 'OPEN' })}>
                  Reopen
                </Button>
                <Button variant="secondary" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => setSelected(new Set(rows.map((slot) => slot.id)))}>
                Select the whole night
              </Button>
            )}
          </div>
        ) : null}

        <Table headers={['', 'Time', 'Status', 'Capacity', 'Sold', 'Comped', 'Held', 'Left', 'Walked in', 'No-shows', '']}>
          {rows.length === 0 ? <EmptyRow columns={11}>No entry times yet.</EmptyRow> : null}
          {rows.map((slot) => (
            <SlotRow
              key={slot.id}
              slot={slot}
              timezone={timezone}
              manage={manage}
              checked={selected.has(slot.id)}
              onToggle={() => toggle(slot.id)}
              onPatch={(input) => patch.mutate({ slotId: slot.id, input })}
              busy={patch.isPending}
            />
          ))}
        </Table>
      </Panel>
    </div>
  );
}

function SlotRow({
  slot,
  timezone,
  manage,
  checked,
  onToggle,
  onPatch,
  busy,
}: {
  slot: TimedEntrySlot;
  timezone: string;
  manage: boolean;
  checked: boolean;
  onToggle: () => void;
  onPatch: (input: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const [capacity, setCapacity] = useState(String(slot.units));
  const dirty = capacity.trim() !== String(slot.units);
  const shrinkingBelowSold = dirty && Number(capacity) < slot.committed;

  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-3 py-2">{manage ? <input type="checkbox" checked={checked} onChange={onToggle} aria-label={`Select ${clock(slot.starts_at, timezone)}`} /> : null}</td>
      <td className="px-3 py-2 font-medium text-ink-900">{clock(slot.starts_at, timezone)}</td>
      <td className="px-3 py-2">
        <Badge tone={STATUS_TONE[slot.status] ?? 'ok'}>{slot.status.replaceAll('_', ' ').toLowerCase()}</Badge>
      </td>
      <td className="px-3 py-2">
        {manage ? (
          <span className="flex items-center gap-2">
            <input
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              inputMode="numeric"
              className={`${INPUT} w-16`}
              aria-label={`Capacity at ${clock(slot.starts_at, timezone)}`}
            />
            {dirty ? (
              <Button
                disabled={busy}
                onClick={() => {
                  if (shrinkingBelowSold) {
                    const ok = window.confirm(
                      `${clock(slot.starts_at, timezone)} has ${slot.committed} admissions already sold, comped or held. Setting it to ${capacity} takes it below that. The tickets already sold stay valid. Continue?`,
                    );
                    if (!ok) return;
                  }
                  onPatch({ capacity: Number(capacity), allow_below_committed: shrinkingBelowSold });
                }}
              >
                Save
              </Button>
            ) : null}
          </span>
        ) : (
          slot.units
        )}
      </td>
      <td className="px-3 py-2 tabular">{slot.paid}</td>
      <td className="px-3 py-2 tabular">{slot.comped}</td>
      <td className="px-3 py-2 tabular">{slot.held + slot.payment_pending}</td>
      <td className="px-3 py-2 tabular font-medium">{slot.available}</td>
      <td className="px-3 py-2 tabular">{slot.scanned}</td>
      <td className="px-3 py-2 tabular">{slot.no_shows}</td>
      <td className="px-3 py-2 text-right">
        {manage ? (
          <Button variant="secondary" disabled={busy} onClick={() => onPatch({ status: slot.status === 'CLOSED' ? 'OPEN' : 'CLOSED' })}>
            {slot.status === 'CLOSED' ? 'Reopen' : 'Close'}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}

/** Building a night: one description, every time in it. */
function Generator({
  detail,
  performanceId,
  gaAreas,
  onDone,
  onError,
  disabled,
}: {
  detail: PanelProps['detail'];
  performanceId: string;
  gaAreas: { id: string; code: string; name: string }[];
  onDone: () => void;
  onError: (cause: ApiError) => void;
  disabled: boolean;
}) {
  const timedTypes = detail.ticket_types.filter((type) => type.timed_entry);
  const [ticketTypeId, setTicketTypeId] = useState(timedTypes[0]?.id ?? '');
  const [gaAreaId, setGaAreaId] = useState(gaAreas[0]?.id ?? '');
  const [first, setFirst] = useState('19:00');
  const [last, setLast] = useState('23:00');
  const [interval, setInterval] = useState('15');
  const [capacity, setCapacity] = useState('10');
  const [early, setEarly] = useState('5');
  const [late, setLate] = useState('15');
  const [allNights, setAllNights] = useState(false);

  const generate = useMutation({
    mutationFn: () =>
      timedEntryEndpoints.generate(detail.event.id, {
        performance_ids: allNights ? detail.performances.map((p) => p.id) : [performanceId],
        ticket_type_id: ticketTypeId || null,
        ga_area_id: gaAreaId,
        first_entry_time: first,
        last_entry_time: last,
        interval_minutes: Number(interval),
        default_capacity: Number(capacity),
        early_entry_minutes: early === '' ? null : Number(early),
        late_entry_minutes: late === '' ? null : Number(late),
      }),
    onSuccess: onDone,
    onError,
  });

  const per = Math.floor((toMinutes(last) - toMinutes(first)) / Math.max(1, Number(interval))) + 1;
  const nights = allNights ? detail.performances.length : 1;

  return (
    <Panel
      title="Entry times"
      description="Entry times are wall-clock times on each night, and each one holds real admissions — the same inventory a seat is, so holds, comps, refunds and scanning all work the way they already do."
    >
      {timedTypes.length === 0 ? (
        <p className="px-5 py-4 text-sm text-ink-600">
          No ticket type on this event is sold by entry time. Mark one as timed entry under Tickets &amp; zones first.
        </p>
      ) : gaAreas.length === 0 ? (
        <p className="px-5 py-4 text-sm text-ink-600">This manifest has no admission area. Add one to the manifest first; the entry times draw their capacity from it.</p>
      ) : (
        <>
          <div className="grid gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Ticket type" htmlFor="te-type">
              <select id="te-type" value={ticketTypeId} onChange={(e) => setTicketTypeId(e.target.value)} className={INPUT}>
                {timedTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Admission area" htmlFor="te-area">
              <select id="te-area" value={gaAreaId} onChange={(e) => setGaAreaId(e.target.value)} className={INPUT}>
                {gaAreas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="First entry" htmlFor="te-first">
              <input id="te-first" type="time" value={first} onChange={(e) => setFirst(e.target.value)} className={INPUT} />
            </Field>
            <Field label="Last entry" htmlFor="te-last">
              <input id="te-last" type="time" value={last} onChange={(e) => setLast(e.target.value)} className={INPUT} />
            </Field>
            <Field label="Every (minutes)" htmlFor="te-interval">
              <input id="te-interval" value={interval} onChange={(e) => setInterval(e.target.value)} inputMode="numeric" className={INPUT} />
            </Field>
            <Field label="People per time" htmlFor="te-capacity">
              <input id="te-capacity" value={capacity} onChange={(e) => setCapacity(e.target.value)} inputMode="numeric" className={INPUT} />
            </Field>
            <Field label="Early grace (minutes)" htmlFor="te-early">
              <input id="te-early" value={early} onChange={(e) => setEarly(e.target.value)} inputMode="numeric" className={INPUT} />
            </Field>
            <Field label="Late grace (minutes)" htmlFor="te-late">
              <input id="te-late" value={late} onChange={(e) => setLate(e.target.value)} inputMode="numeric" className={INPUT} />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-4 border-t border-line px-5 py-3">
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input type="checkbox" checked={allNights} onChange={(e) => setAllNights(e.target.checked)} />
              Build every night on this event ({detail.performances.length})
            </label>
            <p className="text-sm text-ink-600">
              {Number.isFinite(per) && per > 0 ? `${per} times a night · ${per * Number(capacity || 0)} admissions a night · ${per * Number(capacity || 0) * nights} in total` : 'Check the times.'}
            </p>
            <Button className="ml-auto" disabled={disabled || generate.isPending || !gaAreaId || !(per > 0)} onClick={() => generate.mutate()}>
              {generate.isPending ? 'Building…' : 'Build entry times'}
            </Button>
          </div>
        </>
      )}
    </Panel>
  );
}

const toMinutes = (value: string): number => {
  const [h, m] = value.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
